import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { ktx2Extend } from '../utils/ktx2';
import { assetUrl } from '../services/assets';
import { useWorldStore } from '../state/worldStore';
import { listWorldObjects } from '../services/worldObjects';
import { listWorldAssets, assetUrlFor } from '../services/worldAssets';
import { TOWN_LOCATIONS, ENTRY_VIA_PORTAL } from '../rooms';
import CameraBasisProbe from './CameraBasisProbe';

// Portals — the doors between the street and the places under it.
//
// A subway entrance is not a venue's exterior, it is a PLACED INSTANCE of a
// shared asset: there is one entrance model and there will be one on every other
// corner. That distinction decides the whole shape of this. A venue `shell` is
// one-per-venue and could never be the tenth entrance; a `world_object` with
// kind 'portal' is a row, so the tenth costs a row — and because every one of
// them carries the same `asset_id`, they can be issued as a single instanced
// draw rather than ten.
//
// It is also what the spec meant by `interior_ref`: "if enterable, which
// interior world" (§1). `ref` is the room id this opens into.
//
// WHY NOT A MARKER PIN. VenueMarkers floats a clickable label over a venue,
// which is fine for a shopfront and undercuts a building whose entire design is
// a mouth you walk into. The trigger is a volume you enter — Design Law 1, the
// function disappears into the geography.

// Where the trigger sits when a portal has not been tuned yet: a box in front of
// the entrance, at head height, big enough to walk into without aiming. Tuned
// per portal in the panel and saved to `data.trigger`, because where the mouth
// is depends on the model and no default can know that.
const DEFAULT_TRIGGER = { x: 0, y: 0.5, z: 0, w: 2, h: 2, d: 2 };

const trigOf = (o) => ({ ...DEFAULT_TRIGGER, ...(o.data?.trigger || {}) });

// WHAT A PORTAL OPENS ONTO — a room, or another portal.
//
// Transit is why this is two things and not one. Entering the underground is a
// portal that opens onto a ROOM. Coming back up is not: the whole point of a
// subway is that you surface somewhere you did not start, so the platform's exit
// opens onto ANOTHER PORTAL — the station at the far end — and you step out of
// that one, on that street.
//
// Targeting the portal rather than a coordinate is what makes it a network. Move
// the destination station and every route into it follows, because the route
// points at the station rather than at where the station used to be. It also
// chains: if the far end grows its own hallway, the exit points at a second room
// whose exit points at the street portal, and nothing here changes.
const targetOf = (o) => o.data?.target || { kind: 'room', ref: o.ref };

// You surface IN FRONT of the station, facing away from it — the direction a
// person emerging actually looks, and it puts the new neighbourhood in view
// instead of the doorway you just came out of.
const EMERGE = 3;
const arrivalAt = (portal, eye) => {
  const rot = portal.rotY || 0;
  // The placement tool stores rotY as atan2(-fx, -fz) of the camera that placed
  // it, so this inverts that to recover the direction the entrance faces.
  const fx = -Math.sin(rot); const fz = -Math.cos(rot);
  const x = portal.pos[0] - fx * EMERGE;
  const z = portal.pos[2] - fz * EMERGE;
  return { pos: [x, eye, z], look: [x - fx * 6, eye, z - fz * 6] };
};

// The asset's own origin is the placement point, and its y=0 is the pavement.
//
// Both are the street-prop convention, and both matter here: this entrance
// carries five metres of stairwell BELOW y=0, so grounding it the way a venue is
// grounded — drop the lowest point to zero — would bury the street and hang the
// canopy two units in the air. `detectFloor` cannot save us either; it looks for
// a wide flat mesh near the BOTTOM, which in this model is the floor of the
// shaft. The model already says where the ground is. Believe it.
function PortalModel({ url, fit }) {
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(assetUrl(url), true, true, ktx2);

  const model = useMemo(() => {
    const m = scene.clone(true);
    // Same guard the shells carry: a baked light that escapes into the open city
    // overwhelms the day/night rig for everything around it.
    const doomed = [];
    m.traverse((o) => { if (o.isLight) doomed.push(o); });
    for (const l of doomed) l.parent?.remove(l);
    if (doomed.length) console.warn(`[portal] removed ${doomed.length} baked light(s) from ${url}`);

    m.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(m);
    const size = new THREE.Vector3(); box.getSize(size);
    const longest = Math.max(size.x, size.z) || 1;
    const s = fit ? fit / longest : 1;
    m.scale.setScalar(s);
    const g = new THREE.Group();
    g.add(m);
    return g;
  }, [scene, fit, url]);

  return <primitive object={model} />;
}

function Portal({ row, url, editing, selected }) {
  const t = trigOf(row);
  return (
    <group position={row.pos} rotation={[0, row.rotY || 0, 0]}>
      {url ? (
        <React.Suspense fallback={null}>
          <PortalModel url={url} fit={row.size || 6.7} />
        </React.Suspense>
      ) : null}
      {/* The trigger, drawn only while placing. An invisible volume is
          impossible to aim, and every mis-set one reads as "the door is
          broken" rather than "the box is six inches to the left". */}
      {editing && (
        <mesh position={[t.x, t.y, t.z]}>
          <boxGeometry args={[t.w, t.h, t.d]} />
          <meshBasicMaterial color={selected ? '#ffb25e' : '#7dd3ff'} wireframe transparent opacity={selected ? 0.9 : 0.35} />
        </mesh>
      )}
    </group>
  );
}

export default function Portals({ eye = 0.75 }) {
  const [all, setAll] = useState([]);
  const [assets, setAssets] = useState([]);
  const interior = useWorldStore((s) => s.interior);
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const setSpawnOverride = useWorldStore((s) => s.setSpawnOverride);
  const editing = useWorldStore((s) => s.adminTool) === 'portal';
  const sel = useWorldStore((s) => s.portalSel);
  const version = useWorldStore((s) => s.portalVersion);

  // EVERY zone's portals, not just this one. A platform exit has to resolve a
  // target standing on a street that is not loaded while you are underground, so
  // filtering the fetch by zone would make the far end unreachable.
  useEffect(() => {
    let dead = false;
    listWorldObjects(null, 'portal').then((r) => { if (!dead) setAll(r || []); });
    listWorldAssets().then((a) => { if (!dead) setAssets(a || []); });
    return () => { dead = true; };
  }, [version]);

  // WHICH VENUES HAVE NO WAY IN.
  //
  // ENTRY_VIA_PORTAL retires the building-as-hit-zone, so a venue with no portal
  // row can no longer be WALKED into. Its marker and the map still work, so this
  // is not a lockout — but walking up to a building and having it ignore you is
  // indistinguishable from a broken click handler, and it is the mode the guard,
  // the doorway and the whole change are about.
  //
  // So say it, by name, once per load. The fix — place a door — is not something
  // anyone would guess from a building that simply does nothing.
  useEffect(() => {
    if (!ENTRY_VIA_PORTAL || !all.length) return;
    const reachable = new Set(all.filter((o) => o.zone_id === 'street').map((o) => (o.data?.target?.ref) || o.ref));
    const orphans = TOWN_LOCATIONS.filter((l) => l.interiorId && !reachable.has(l.interiorId));
    if (!orphans.length) { console.log(`[portal] all ${TOWN_LOCATIONS.length} venue(s) have a door`); return; }
    console.warn(`[portal] ${orphans.length} venue(s) have NO placed door — you cannot walk into them `
      + '(their marker and the map still work): '
      + orphans.map((l) => `${l.name} (${l.interiorId})`).join(', ')
      + ' — place one with ⚙ → Portal, or add ?hitzone=1 to restore the old click-the-building entry for this session.');
  }, [all]);

  // The zone you are standing in: a room id inside, 'street' outside. Only these
  // are drawn and only these can fire.
  const zone = interior || 'street';
  const rows = useMemo(() => all.filter((o) => o.zone_id === zone), [all, zone]);
  const byObjectId = useMemo(() => new Map(all.map((o) => [o.object_id, o])), [all]);

  // THE HOLE THIS STATION STANDS IN.
  //
  // The entrance carries five metres of stairwell below y=0, and until the ground
  // opens under it none of that is visible — the model reads as a canopy sitting
  // on tarmac with its stairs buried. A portal that declares `data.pit` tells the
  // ground to open there, so the station sinks in rather than resting on top.
  //
  // Published rather than fetched by the ground, because the portals are already
  // in hand here. Two fetches would be two chances for the hole and the station
  // to end up in different places.
  const setGroundPits = useWorldStore((s) => s.setGroundPits);
  useEffect(() => {
    const street = all.filter((o) => o.zone_id === 'street');
    // A station's opening is a rectangle, because the thing standing in it is.
    // A circle either leaves tarmac inside the stairwell's corners or eats the
    // pavement outside its long sides — there is no radius that fits a 6x14
    // entrance. `rotY` travels with it so the hole stays square to the stairs
    // when the station is turned.
    //
    // `r` is still honoured as a square, so pits saved before this reads as
    // something sensible rather than vanishing.
    const pits = street
      .filter((o) => o.data?.pit && (o.data.pit.w > 0 || o.data.pit.r > 0))
      .map((o) => {
        const p = o.data.pit;
        return {
          x: o.pos[0], z: o.pos[2], rotY: o.rotY || 0,
          w: p.w > 0 ? p.w : p.r * 2,
          d: p.d > 0 ? p.d : (p.w > 0 ? p.w : p.r * 2),
        };
      });
    setGroundPits(pits);
    // SAY WHICH OF THE TWO REASONS IT IS.
    //
    // "The ground is still there" has exactly two causes and they need opposite
    // fixes: no pit was ever saved, or the pit is saved and the ground being
    // looked at is not the one that can be cut. Printing whether `data` survived
    // the round trip separates them in one line — and a portal that has a pit
    // radius in the panel but no `data` here means the API is dropping it,
    // which means it has not been restarted.
    if (!street.length) return;
    const withData = street.filter((o) => o.data && Object.keys(o.data).length).length;
    console.log(`[portal] ${street.length} street portal(s), ${withData} carrying data, ${pits.length} ground pit(s)`
      + (pits.length ? `: ${pits.map((p) => `${p.w}x${p.d} @ ${p.x.toFixed(0)},${p.z.toFixed(0)}`).join(' · ')}` : '')
      + (!withData ? ' — NO portal has any `data`. Set a pit radius and if this still reads 0, the API is dropping the field: restart it.' : ''));
  }, [all, setGroundPits]);

  const byId = useMemo(() => new Map(assets.map((a) => [a.assetId, a])), [assets]);

  // ARMED ONLY AFTER YOU LEAVE.
  //
  // Coming back UP the stairs puts you standing in the trigger you just used, so
  // a naive "am I inside the box" test re-enters immediately and the player is
  // trapped in a loop they cannot walk out of. A portal therefore starts
  // disarmed and only becomes live once the player has been outside it — which
  // also means spawning next to one is safe.
  const armed = useRef(new Map());
  const local = useRef(new THREE.Vector3());
  // WHERE YOU WERE LAST FRAME, so the test is a SWEPT one.
  //
  // The trigger used to test a single point, which is fine for a 2-unit box and
  // fails for a door. Street walking is 9 units/sec and running is 2.2× that, so
  // at 60fps a running player moves 0.33 units between frames and at 30fps
  // 0.66 — further than the depth of any box thin enough to sit on an actual
  // doorway without also catching people on the pavement. The door then works at
  // a walk, works when you approach it slowly, and silently fails when you run
  // at it, which is the worst possible way for a door to be broken.
  //
  // So the box is tested against the SEGMENT you travelled, not the point you
  // landed on. This is what makes a thin trigger usable at all — without it,
  // "place it exactly on the door" and "the door opens reliably" are in direct
  // conflict and the only resolution is an over-wide box.
  const prev = useRef(null);

  const fire = (row) => {
    const t = targetOf(row);
    console.log(`[portal] #${row.object_id} fired → ${t?.kind || '?'}:${t?.ref ?? 'NOTHING'}`);
    if (!t?.ref) {
      console.warn(`[portal] #${row.object_id} has no target. Its \`ref\` is ${JSON.stringify(row.ref)} and data.target is ${JSON.stringify(row.data?.target)} — set one with "Opens onto" in the Portals panel.`);
      return;
    }
    if (t.kind === 'portal') {
      const dest = byObjectId.get(Number(t.ref));
      if (!dest) { console.warn(`[portal] #${row.object_id} points at portal #${t.ref}, which no longer exists`); return; }
      // ARRIVE STANDING AT THE DESTINATION PORTAL, wherever it lives. The pose
      // comes from the destination's own transform, so moving that station moves
      // every route that arrives at it.
      //
      // Two cases, one rule. A portal on the STREET means surfacing: leave the
      // room and land in front of it. A portal in a ROOM means the train pulling
      // in at the far platform — enter that room, but at the spot where the
      // train stops rather than at the room's front door. `enterInterior` clears
      // spawnOverride, so the pose is applied after it, not with it.
      const pose = arrivalAt(dest, eye);
      if (dest.zone_id === 'street') { exitInterior(pose); return; }
      enterInterior(dest.zone_id);
      setSpawnOverride(pose);
      return;
    }
    enterInterior(t.ref);
  };

  useFrame(({ camera }) => {
    const from = prev.current;
    const cx = camera.position.x; const cy = camera.position.y; const cz = camera.position.z;
    // A TELEPORT IS NOT A WALK. Entering a room, surfacing from the subway and
    // fast-travelling to an avatar all move the camera hundreds of units in one
    // frame, and sweeping that line would drag you through every door between
    // the two points — arriving somewhere would fire whatever happened to be on
    // the way. Beyond a stride, only the destination counts.
    const step = from ? Math.hypot(cx - from.x, cy - from.y, cz - from.z) : 0;
    const swept = from && step > 0 && step < 5;

    for (const row of rows) {
      const id = row.object_id;
      const t = trigOf(row);
      // World → portal-local: undo the translation, then the rotation. Doing
      // only the first is the bug that makes a rotated portal trigger off to one
      // side of where its box is drawn.
      const a = -(row.rotY || 0);
      const c = Math.cos(a); const s = Math.sin(a);
      const toLocal = (wx, wy, wz) => {
        local.current.set(wx - row.pos[0], wy - row.pos[1], wz - row.pos[2]);
        return {
          x: local.current.x * c + local.current.z * s,
          y: local.current.y,
          z: -local.current.x * s + local.current.z * c,
        };
      };
      const contains = (p) => Math.abs(p.x - t.x) <= t.w / 2
        && Math.abs(p.y - t.y) <= t.h / 2
        && Math.abs(p.z - t.z) <= t.d / 2;

      const now = toLocal(cx, cy, cz);
      let inside = contains(now);

      // Walk the segment in steps no longer than the box's THINNEST dimension,
      // so nothing can fit between two samples. Rotation and translation are
      // affine, so interpolating in local space is the same line as
      // interpolating in world space — and it converts once per endpoint rather
      // than once per sample. Capped because a low frame rate must cost frames,
      // not a loop that grows without limit.
      if (!inside && swept) {
        const was = toLocal(from.x, from.y, from.z);
        const thinnest = Math.max(0.05, Math.min(t.w, t.h, t.d));
        const n = Math.min(24, Math.ceil(step / thinnest));
        for (let i = 1; i < n && !inside; i += 1) {
          const k = i / n;
          inside = contains({
            x: was.x + (now.x - was.x) * k,
            y: was.y + (now.y - was.y) * k,
            z: was.z + (now.z - was.z) * k,
          });
        }
      }

      // SAY WHEN YOU CROSS THE EDGE. "It stopped warping me" has three causes
      // that look identical from inside the world — you are never entering the
      // box, you are entering it while it is disarmed, or it fires and the
      // target is empty. Logging the transition rather than the state keeps this
      // to one line per crossing instead of one per frame.
      const was = !!armed.current.get(`in:${id}`);
      if (inside !== was) {
        armed.current.set(`in:${id}`, inside);
        console.log(`[portal] #${id} trigger ${inside ? 'ENTERED' : 'left'} · armed=${!!armed.current.get(id)}`
          + ` · you ${camera.position.x.toFixed(1)},${camera.position.y.toFixed(1)},${camera.position.z.toFixed(1)}`
          + ` · box local ${t.x},${t.y},${t.z} size ${t.w}x${t.h}x${t.d} at ${row.pos.map((n) => n.toFixed(1)).join(',')}`);
      }

      if (!inside) { armed.current.set(id, true); continue; }
      if (!armed.current.get(id)) continue;
      armed.current.set(id, false);
      fire(row);
    }

    // Recorded even when there are no rows, so the first frame after a portal
    // loads has a real previous position instead of treating that frame as a
    // teleport.
    if (from) { from.x = cx; from.y = cy; from.z = cz; } else prev.current = { x: cx, y: cy, z: cz };
  });

  return (
    <group>
      {/* "Place ahead of me" reads the camera from a module-level basis, which
          only stays current while something inside the Canvas is updating it. */}
      {editing && <CameraBasisProbe />}
      {rows.map((row) => (
        <Portal
          key={row.object_id}
          row={row}
          url={assetUrlFor(byId.get(row.assetId)) || row.data?.model || null}
          editing={editing}
          selected={sel === row.object_id}
        />
      ))}
    </group>
  );
}
